/**
 * Centralized secret sale: session, PDP, product card, Quick Buy, form flag.
 * Triggered only from product-info, password-gate, and product cards rendered with
 * load_secret_sale_js (.product-card-secret-sale class) (events or SecretSaleSession).
 * No theme.js calls; Quick Buy and form-flag run via DOM observation.
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'pgate:sale-secret:v2';

  // Generic code verification (cart-aware, cached) lives in assets/discount-verify.js,
  // exposed as window.DiscountVerify. It is not secret-sale specific; here we only add
  // the secret-sale interpretation on top of the shared verdict.
  function verifyDiscount(code) {
    return window.DiscountVerify.verify(code).then(function (data) {
      return data && data.secret_sale === true ? data : null;
    });
  }

  function getRegisteredSaleParams() {
    const params = window.__secretSaleParams;
    if (!Array.isArray(params)) return [];
    return params
      .map(function (p) { return String(p ?? '').trim(); })
      .filter(function (p) { return p.length > 0; });
  }

  function getSaleParamFromUrl() {
    const sale = new URLSearchParams(location.search).get('sale');
    if (!sale) return null;
    const registered = getRegisteredSaleParams();
    if (registered.length && registered.indexOf(sale) === -1) return null;
    return sale;
  }

  function getSaleParam() {
    const session = readSession();
    if (isValidSession(session) && session.saleParam) {
      return session.saleParam;
    }
    const fromUrl = getSaleParamFromUrl();
    if (fromUrl) return fromUrl;
    const registered = getRegisteredSaleParams();
    return registered[0] || null;
  }

  function readSession() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY));
    } catch (_) {
      return null;
    }
  }

  function isValidSession(s) {
    return !!(s && s.token === 'ok' && typeof s.expiresAt === 'number' && Date.now() < s.expiresAt);
  }

  function isActive() {
    return isValidSession(readSession());
  }

  function writeSession(ttlMs, saleParam, offerValue, discountCode) {
    const payload = { token: 'ok', expiresAt: Date.now() + ttlMs };
    if (saleParam) payload.saleParam = String(saleParam).trim();
    if (offerValue) payload.offerValue = String(offerValue).trim();
    if (discountCode) payload.discountCode = String(discountCode).trim();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }

  // Guards against sending pasted gift-card codes or stray keystrokes to the endpoint.
  const CODE_MIN_LENGTH = 3;
  const CODE_MAX_LENGTH = 64;

  // Matches the `session_validity` (25 min) configured on every password-gate section instance
  // across the sale collection templates as of writing.
  const TRY_ACTIVATE_TTL_MS = 25 * 60 * 1000;

  // Cart-line attribute value set when a manual discount code is applied, instead of the
  // plain-code default ("summer-sale"). The manual-discount Custom Price Discount and the
  // automatic secret-sale discount both key off this attribute; reusing "summer-sale" made both
  // eligible at once and Shopify's combination rules silently kept the automatic one instead of
  // the code. A distinct value keeps them from ever colliding.
  const DISCOUNT_OFFER_VALUE = 'secret-sale-discount-code';

  /**
   * Verifies a typed/pasted code against `discounts/verify` and, on a match, activates the
   * secret sale in place (writes the session and, if the code is tied to a real discount,
   * applies it via `options.applyDiscountCode`). Resolves true when activation happened, so
   * the caller can skip its own "invalid code" handling.
   *
   * `discounts/verify` answers two shapes for a match: `{secret_sale:true, discount:null}` for a
   * plain secret-sale code (nothing to apply — only the session gets written), and
   * `{secret_sale:true, discount:"<code>"}` when the typed value is tied to an actual manual
   * discount tagged secret-sale.
   */
  async function tryActivate(code, options) {
    const value = String(code ?? '').trim();
    if (value.length < CODE_MIN_LENGTH || value.length > CODE_MAX_LENGTH) return false;

    const payload = await verifyDiscount(value);
    if (!payload) return false;

    const discountCode = String(payload.discount ?? '').trim();
    // Best-effort label only — session validity never depends on it (see isValidSession).
    // Falls back through: current session's own saleParam → this page's `?sale=` (if it matches
    // a registered password-gate section) → the first password-gate section registered on this
    // page. May resolve to null on pages with none of those (e.g. the cart) — that's fine.
    writeSession(TRY_ACTIVATE_TTL_MS, getSaleParam(), discountCode ? DISCOUNT_OFFER_VALUE : undefined, discountCode || undefined);

    if (discountCode) {
      if (options && typeof options.applyDiscountCode === 'function') {
        try {
          await options.applyDiscountCode(discountCode);
        } catch (_) {}
      }
    } else {
      if (options && typeof options.onRecognized === 'function') {
        try {
          options.onRecognized();
        } catch (_) {}
      }

      if (options && typeof options.afterActivate === 'function') {
        try {
          await options.afterActivate();
        } catch (_) {}
      }
    }

    // Product cards, PDP pricing, and the existing cart were already scanned/rendered while the
    // session was inactive — re-run that now instead of requiring a reload.
    activateSecretSaleUi();

    return true;
  }

  function hasParam() {
    const sale = new URLSearchParams(location.search).get('sale');
    if (!sale) return false;
    const registered = getRegisteredSaleParams();
    if (registered.length) return registered.indexOf(sale) !== -1;
    return false;
  }

  function moneyFormat(amount, currency) {
    const nf = new Intl.NumberFormat('de-DE', { style: 'currency', currency: currency });
    const parts = nf.formatToParts(Number(amount));
    const numberStr = parts
      .filter(function (p) { return p.type !== 'currency' && p.type !== 'literal'; })
      .map(function (p) { return p.value; })
      .join('');
    const symbol = (parts.find(function (p) { return p.type === 'currency'; }) || {}).value || currency;
    return numberStr + ' ' + symbol;
  }

  function moneyHtml(amount, currency) {
    return '<span class="money">' + moneyFormat(amount, currency) + '</span>';
  }

  function getShopCurrency(config) {
    return (
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      (config && config.currency) ||
      (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.cartCurrency) ||
      'EUR'
    );
  }

  // Commercial ("charm") price: keep the whole units, force the decimals to .99, so an
  // FX-converted secret price shows a clean price (e.g. 1141.42 -> 1141.99) instead of an
  // arbitrary fraction. MUST stay identical to the offer-discount Shopify Function
  // (extensions/offer-discount/src/run.ts, commercialPrice) so theme and checkout match 1:1.
  function commercialPrice(amount) {
    if (window.PresentmentMoney && window.PresentmentMoney.commercial) {
      return window.PresentmentMoney.commercial(amount);
    }
    const n = Number(amount);
    if (!Number.isFinite(n)) return amount;
    if (n <= 0) return n;
    return Math.floor(n) + 0.99;
  }

  function toPresentment(value) {
    if (value == null) return value;
    if (!window.PresentmentMoney) return value;
    const converted = window.PresentmentMoney.convert(value);
    // In the base currency (rate 1) show the merchant's exact price; only charm conversions.
    return window.PresentmentMoney.rate() === 1 ? converted : commercialPrice(converted);
  }

  function getCardVariantId(root, config) {
    const fromUrl = root.querySelector('a')?.getAttribute('href')?.match(/variant=(\d+)/);
    if (fromUrl && Object.prototype.hasOwnProperty.call(config.secretByVid, fromUrl[1])) {
      return fromUrl[1];
    }
    return String(config.featuredVid || '');
  }

  function shouldSkipCardPriceApply(root, config) {
    const vid = getCardVariantId(root, config);
    return (
      root.getAttribute('data-secret-sale-processed') === 'true' &&
      root.getAttribute('data-secret-sale-vid') === vid
    );
  }

  function markCardPriceApplied(root, config) {
    root.setAttribute('data-secret-sale-vid', getCardVariantId(root, config));
    root.setAttribute('data-secret-sale-processed', 'true');
  }

  function parseOptionalPrice(val) {
    if (val === null || val === '') return null;
    const n = parseFloat(val);
    return Number.isFinite(n) ? n : null;
  }

  function getSecretSaleReferencePrice(config, vid, secretPrice, regularPrice) {
    const vidKey = String(vid);
    const compareAt = config.compareAtByVid && config.compareAtByVid[vidKey];
    return getSecretSaleReferencePriceFromValues(secretPrice, regularPrice, compareAt);
  }

  function getSecretSaleReferencePriceFromValues(secretPrice, regularPrice, compareAt) {
    if (typeof compareAt === 'number' && compareAt > secretPrice) return compareAt;
    return regularPrice;
  }

  // Shared by the PDP, product-card, and Quick Buy price renderers: the "current" price is
  // whichever `.price` is already flagged `--highlight`, else the first non-compare one, else
  // just the first `.price` found.
  function findCurrentPriceEl(container) {
    return container.querySelector('.price.price--highlight')
      || Array.prototype.find.call(container.querySelectorAll('.price'), function (p) { return !p.classList.contains('price--compare'); })
      || container.querySelector('.price');
  }

  // Creates the `.price--compare` element next to `curr` if the markup doesn't already have one
  // (themes only render a compare-at price when a product already has one at page-render time).
  function ensureComparePriceEl(container, curr, compareClassName, insertPosition) {
    let comp = container.querySelector('.price.price--compare');
    if (!comp && curr) {
      comp = document.createElement('span');
      comp.className = compareClassName;
      const vh = document.createElement('span');
      vh.className = 'visually-hidden';
      vh.textContent = 'Regular price';
      comp.appendChild(vh);
      curr.insertAdjacentElement(insertPosition, comp);
    }
    return comp;
  }

  function updateOnlyNumberInLabel(rootEl, formatted) {
    if (!rootEl) return;
    const span = rootEl.querySelector('.saving-amount');
    if (span) {
      span.classList.add('money');
      span.textContent = ' ' + formatted;
      return;
    }
    const walker = document.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT);
    const re = /(?:€|\$|£|USD|EUR|GBP|CHF|RSD)?\s*\d+(?:[.,\s]\d{3})*(?:[.,]\d{1,2})?/;
    let node;
    while ((node = walker.nextNode())) {
      const m = node.nodeValue && node.nodeValue.match(re);
      if (!m) continue;
      const before = node.nodeValue.slice(0, m.index);
      const after = node.nodeValue.slice(m.index + m[0].length);
      const wrap = document.createElement('span');
      wrap.className = 'saving-amount money';
      wrap.textContent = ' ' + formatted;
      const frag = document.createDocumentFragment();
      if (before) frag.appendChild(document.createTextNode(before));
      frag.appendChild(wrap);
      if (after) frag.appendChild(document.createTextNode(after));
      node.parentNode.replaceChild(frag, node);
      break;
    }
  }

  function setMoneyOnEl(el, amount, label, currency) {
    if (!el) return;
    const amountKey = String(amount) + ':' + currency;
    if (el.getAttribute('data-secret-sale-amount') === amountKey) return;
    const vhPrev = el.querySelector('.visually-hidden');
    el.textContent = '';
    const vh = document.createElement('span');
    vh.className = 'visually-hidden';
    vh.textContent = vhPrev ? vhPrev.textContent : label;
    el.appendChild(vh);
    el.appendChild(document.createTextNode(' '));
    const moneyEl = document.createElement('span');
    moneyEl.className = 'money';
    moneyEl.textContent = moneyFormat(amount, currency);
    el.appendChild(moneyEl);
    el.setAttribute('data-secret-sale-amount', amountKey);
  }

  function scheduleAfterThemeAtc(fn) {
    requestAnimationFrame(function () {
      requestAnimationFrame(fn);
    });
  }

  function scheduleAfterVariantPriceUpdate(fn) {
    scheduleAfterThemeAtc(fn);
  }

  function isQuickBuyOpen(root) {
    return !!(root && (root.open === true || root.hasAttribute('open')));
  }

  function quickBuyVariantEventMatchesRoot(root, event) {
    const form = root.querySelector('form[action*="/cart/add"]');
    if (!form) return false;
    const variant = event.detail && event.detail.variant;
    const idInput = form.querySelector('input[name="id"]');
    if (variant && idInput && String(idInput.value) !== String(variant.id)) return false;
    if (event.target === form) return true;
    const productVariants = root.querySelector('product-variants');
    return !!(productVariants && (event.target === productVariants || productVariants.contains(event.target)));
  }

  function resetQuickBuySecretSaleBindings(root) {
    if (!root) return;
    if (root.__secretSaleQbPriceMo) {
      root.__secretSaleQbPriceMo.disconnect();
      root.__secretSaleQbPriceMo = null;
    }
    root.__secretSaleQbPriceGuard = false;
    if (root.__secretSaleVariantHandler) {
      document.removeEventListener('variant:changed', root.__secretSaleVariantHandler);
      root.__secretSaleVariantHandler = null;
    }
    root.__secretSaleVariantBound = false;
    root.removeAttribute('data-secret-sale-qb-processed');
    root.removeAttribute('data-secret-sale-qb-vid');
  }

  function bindQuickBuyVariantSecretSale(root) {
    if (!root || root.__secretSaleVariantBound) return;
    root.__secretSaleVariantBound = true;

    function onVariantChanged(event) {
      if (!isActive() || !isQuickBuyOpen(root)) return;
      if (!quickBuyVariantEventMatchesRoot(root, event)) return;
      applyQuickBuyUi(root);
    }

    root.__secretSaleVariantHandler = onVariantChanged;
    requestAnimationFrame(function () {
      setTimeout(function () {
        document.addEventListener('variant:changed', onVariantChanged);
      }, 0);
    });
  }

  function initQuickBuySecretSaleRoot(root) {
    if (!root || !isActive()) return;
    resetQuickBuySecretSaleBindings(root);
    bindQuickBuyVariantSecretSale(root);
    scheduleAfterVariantPriceUpdate(function () {
      if (isQuickBuyOpen(root)) applyQuickBuyUi(root);
    });
  }

  function applySecretSaleAtcPrice(scope, secretMoney) {
    if (!scope || !secretMoney) return;
    const priceSlots = scope.querySelectorAll('[data-product-add-button-price]');
    if (!priceSlots.length) return;
    priceSlots.forEach(function (priceSlot) {
      const textRow = priceSlot.closest('.product-form__add-button-text');
      const separator = textRow && textRow.querySelector('[data-product-add-button-separator]');
      if (/<[a-z][\s\S]*>/i.test(secretMoney)) {
        priceSlot.innerHTML = secretMoney;
      } else {
        priceSlot.textContent = secretMoney;
      }
      priceSlot.hidden = false;
      if (separator) separator.hidden = false;
      if (textRow) textRow.classList.add('product-form__add-button-text--has-pricing');
    });
  }

  function clearQuickBuySecretSaleUi(root, info) {
    if (!info) return;
    const priceContainer = info.querySelector('.product-meta__price-list-container') || info;
    priceContainer.querySelectorAll('.sale-percentage-container').forEach(function (el) {
      el.style.display = 'none';
      el.setAttribute('aria-hidden', 'true');
    });
    info.querySelectorAll('.sale-percentage').forEach(function (el) {
      el.style.display = 'none';
      el.classList.add('visually-hidden');
    });
    const priceList = priceContainer.querySelector('.price-list');
    if (priceList) {
      const comp = priceList.querySelector('.price.price--compare');
      if (comp) {
        comp.style.display = 'none';
        comp.classList.add('visually-hidden');
      }
      const curr = priceList.querySelector('.price.price--highlight')
        || Array.prototype.find.call(priceList.querySelectorAll('.price'), function (p) {
          return !p.classList.contains('price--compare');
        });
      if (curr) curr.classList.remove('price--highlight');
    }
    if (!root) return;
    root.querySelectorAll('[data-product-add-button-price]').forEach(function (priceSlot) {
      const textRow = priceSlot.closest('.product-form__add-button-text');
      const separator = textRow && textRow.querySelector('[data-product-add-button-separator]');
      priceSlot.hidden = true;
      if (separator) separator.hidden = true;
      if (textRow) textRow.classList.remove('product-form__add-button-text--has-pricing');
    });
  }

  function applySecretSalePercentageBadge(priceContainer, priceList, pct) {
    if (!priceContainer || pct < 1) return;
    const listEl = priceList || priceContainer.querySelector('.price-list') || priceContainer;

    if (listEl.classList.contains('product-card__price')) {
      let salePct = priceContainer.querySelector('.sale-percentage');
      if (!salePct) {
        salePct = document.createElement('div');
        salePct.className = 'sale-percentage';
        const ve = document.createElement('span');
        ve.className = 'sale-percentage__value';
        salePct.appendChild(ve);
        const curr =
          listEl.querySelector('.price.price--highlight') ||
          Array.prototype.find.call(listEl.querySelectorAll('.price'), function (p) {
            return !p.classList.contains('price--compare');
          }) ||
          listEl.querySelector('.price');
        if (curr) {
          curr.insertAdjacentElement('afterend', salePct);
        } else {
          listEl.appendChild(salePct);
        }
      }
      const cardValueEl = salePct.querySelector('.sale-percentage__value');
      if (cardValueEl) cardValueEl.textContent = '-' + pct + '%';
      salePct.style.display = '';
      salePct.classList.remove('visually-hidden');
      return;
    }

    let container = priceContainer.querySelector('.sale-percentage-container');
    if (!container) {
      container = document.createElement('div');
      container.className = 'sale-percentage-container';
      container.setAttribute('data-product-sale-percentage', '');
      listEl.insertAdjacentElement('afterend', container);
    }

    let salePct = container.querySelector('.sale-percentage');
    if (!salePct) {
      salePct = document.createElement('div');
      salePct.className = 'sale-percentage';
      const ve = document.createElement('span');
      ve.className = 'sale-percentage__value';
      salePct.appendChild(ve);
      container.appendChild(salePct);
    }

    const valueEl = salePct.querySelector('.sale-percentage__value');
    if (valueEl) valueEl.textContent = '-' + pct + '%';

    container.style.display = '';
    container.removeAttribute('aria-hidden');
    salePct.style.display = '';
    salePct.classList.remove('visually-hidden');
  }

  function applySecretSaleMediaBadge(root, visible) {
    const el = root && root.querySelector('.product-card__secret-sale-badge');
    if (!el) return;
    if (visible && isActive()) {
      el.hidden = false;
      el.style.display = '';
    } else {
      el.hidden = true;
      el.style.display = 'none';
    }
  }

  function applyPdpUi(config) {
    const form = document.getElementById(config.formId);
    if (!form) return;
    const productInfo = form.closest('.product__info');
    const meta = productInfo && productInfo.querySelector('.product-meta')
      || document.querySelector('product-meta[form-id="' + config.formId + '"], .product-meta');
    if (!meta) return;
    const priceContainer = meta.querySelector('.product-meta__price-list-container') || meta;
    const priceList = priceContainer.querySelector('.price-list');
    if (!priceList) return;
    const vid = (form.querySelector('input[name="id"]') || {}).value || null;
    if (!vid) return;
    const s = toPresentment(config.secretByVid[vid]);
    const r = config.regularByVid[vid];
    if (s == null || r == null || !(s > 0 && s < r)) return;
    const reference = getSecretSaleReferencePrice(config, vid, s, r);
    let curr = findCurrentPriceEl(priceList);
    let comp = ensureComparePriceEl(priceList, curr, 'price price--compare price--large', 'afterend');
    if (curr) {
      setMoneyOnEl(curr, s, 'Sale price', getShopCurrency(config));
      curr.classList.add('price--highlight');
      if (comp) setMoneyOnEl(comp, reference, 'Regular price', getShopCurrency(config));
    }
    const label = priceContainer.querySelector('.product-meta__label-list .label');
    if (label && reference > s) {
      updateOnlyNumberInLabel(label, moneyFormat(reference - s, getShopCurrency(config)));
    }
    if (reference > s) {
      const pct = Math.round(((reference - s) / reference) * 100);
      applySecretSalePercentageBadge(priceContainer, priceList, pct);
      const atcScope = form.closest('.product-form') || form;
      const atcMoney = moneyHtml(s, getShopCurrency(config));
      scheduleAfterThemeAtc(function () {
        applySecretSaleAtcPrice(atcScope, atcMoney);
      });
    }
  }

  function getCardPriceBlock(root) {
    return root.querySelector('.product-card__price') || root.querySelector('.product-item-meta__price-list-container');
  }

  function updateCardQuickBuyUrls(root) {
    const saleParam = getSaleParam();
    if (!saleParam) return;
    const quickBuyElements = root.querySelectorAll('quick-buy-popover, quick-buy-drawer');
    try {
      quickBuyElements.forEach(function (el) {
        const href = el.getAttribute('href');
        if (!href) return;
        const u = new URL(href, window.location.origin);
        u.searchParams.set('sale', saleParam);
        el.setAttribute('href', u.pathname + u.search);
      });
    } catch (_) { }
  }

  function dispatchCardApply(root, config) {
    root.__secretSaleCfg = config;
    document.dispatchEvent(new CustomEvent('secret-sale:apply-card', { detail: { root: root, config: config } }));
  }

  function initSecretSaleCardRoot(root) {
    if (!root || !isActive()) return;
    if (root.__secretSaleInitialized) return;
    const configEl = root.querySelector('script[data-secret-sale-config]');
    if (!configEl) return;

    let config = null;

    try {
      config = JSON.parse(configEl.textContent || '{}');
    } catch (_) {
      return;
    }

    if (!config || !config.secretByVid || !config.regularByVid) return;

    root.__secretSaleInitialized = true;
    dispatchCardApply(root, config);
    updateCardQuickBuyUrls(root);
    applySecretSaleMediaBadge(root, true);
  }

  function scanSecretSaleCardRoots(scope) {
    if (!scope || !scope.querySelectorAll) return;
    if (scope.matches && (scope.matches('.product-card-secret-sale') || scope.matches('.product-item-secret-sale'))) {
      initSecretSaleCardRoot(scope);
    }
    scope.querySelectorAll('.product-card-secret-sale, .product-item-secret-sale').forEach(initSecretSaleCardRoot);
  }

  function applyCardUi(root, config) {
    if (shouldSkipCardPriceApply(root, config)) return;

    const salePercentageEl = root.querySelector('.sale-percentage');

    const priceBlock = getCardPriceBlock(root);
    if (!priceBlock) {
      if (salePercentageEl) salePercentageEl.style.display = 'none';
      return;
    }
    const vid = getCardVariantId(root, config);
    if (!vid) {
      if (salePercentageEl) salePercentageEl.style.display = 'none';
      return;
    }
    const s = toPresentment(config.secretByVid[vid]);
    const r = config.regularByVid[vid];
    if (s == null || r == null || !(s > 0 && s < r)) {
      if (salePercentageEl) salePercentageEl.style.display = 'none';
      return;
    }
    const currency = getShopCurrency(config);
    const reference = getSecretSaleReferencePrice(config, vid, s, r);
    let curr = findCurrentPriceEl(priceBlock);
    let comp = ensureComparePriceEl(priceBlock, curr, 'price price--compare', 'afterend');
    if (comp) setMoneyOnEl(comp, reference, 'Regular price', currency);
    if (curr) {
      setMoneyOnEl(curr, s, 'Sale price', currency);
      curr.classList.add('price--highlight');
    }
    if (reference > s) {
      const pct = Math.round(((reference - s) / reference) * 100);
      applySecretSalePercentageBadge(priceBlock, priceBlock, pct);
      applySecretSaleMediaBadge(root, true);
    } else {
      if (salePercentageEl) salePercentageEl.style.display = 'none';
      applySecretSaleMediaBadge(root, false);
    }
    markCardPriceApplied(root, config);
  }

  function applyQuickBuyUi(root, isDeferredPass) {
    root.__secretSaleQbApplying = true;
    try {
      const form = root.querySelector('form[action*="/cart/add"]');
      const idInput = form && form.querySelector('input[name="id"]');

      const vid = idInput && idInput.value;
      if (!vid) return;

      const info =
        root.querySelector('.quick-buy-product-card__inner') || root;

      if (!info) return;

      const container = info.querySelector('.qb-secret-prices');
      if (!container) return;

      const spans = Array.prototype.slice.call(
        container.querySelectorAll('span[data-vid]')
      );

      const byVid = {};

      spans.forEach(function (el) {
        const k = String(el.getAttribute('data-vid'));

        byVid[k] = {
          secret: el.getAttribute('data-s'),
          regular: el.getAttribute('data-r'),
          compareAt: parseOptionalPrice(el.getAttribute('data-ca'))
        };
      });

      const entry = byVid[String(vid)];
      if (!entry) return;

      const sale = toPresentment(parseFloat(entry.secret));
      const regular = parseFloat(entry.regular);
      const hasValidSecret = sale > 0 && sale < regular;

      if (!hasValidSecret) {
        clearQuickBuySecretSaleUi(root, info);
        return;
      }

      if (
        !isDeferredPass &&
        root.getAttribute('data-secret-sale-qb-processed') === 'true' &&
        root.getAttribute('data-secret-sale-qb-vid') === String(vid)
      ) {
        return;
      }

      const currency = getShopCurrency(null);

      // SAME STRUCTURE AS PDP
      const priceContainer =
        info.querySelector('.product-meta__price-list-container') || info;

      const priceList = priceContainer.querySelector('.price-list');

      if (!priceList) return;

      let curr = findCurrentPriceEl(priceList);
      let comp = ensureComparePriceEl(priceList, curr, 'price price--compare price--large', 'afterend');

      // UPDATE SALE PRICE
      if (curr) {
        setMoneyOnEl(curr, sale, 'Sale price', currency);
        curr.classList.add('price--highlight');
      }

      const reference = getSecretSaleReferencePriceFromValues(
        sale,
        regular,
        entry.compareAt
      );

      // UPDATE COMPARE PRICE
      if (comp) {
        setMoneyOnEl(comp, reference, 'Regular price', currency);

        comp.style.display = '';
        comp.classList.remove('visually-hidden');
      }

      const pct = Math.round(((reference - sale) / reference) * 100);
      applySecretSalePercentageBadge(priceContainer, priceList, pct);

      const atcMoney = moneyHtml(sale, currency);
      scheduleAfterThemeAtc(function () {
        applySecretSaleAtcPrice(root, atcMoney);
      });

      // OPTIONAL SAVINGS LABEL UPDATE
      const label = priceContainer.querySelector('.product-meta__label-list .label');
      if (label) {
        updateOnlyNumberInLabel(label, moneyFormat(reference - sale, currency));
      }

      root.setAttribute('data-secret-sale-qb-vid', String(vid));
      root.setAttribute('data-secret-sale-qb-processed', 'true');
    } catch (e) {
      console.warn('Secret sale Quick Buy override failed:', e);
    } finally {
      requestAnimationFrame(function () {
        root.__secretSaleQbApplying = false;
        if (isDeferredPass || !isQuickBuyOpen(root)) return;
        scheduleAfterVariantPriceUpdate(function () {
          if (!isActive() || !isQuickBuyOpen(root) || root.__secretSaleQbApplying) return;
          applyQuickBuyUi(root, true);
        });
      });
    }
  }

  const FORM_SELECTOR = 'form[action*="/cart/add"], form[action*="/form/add"]';
  const OFFER_PROP = '_offer';
  const INPUT_NAME = 'properties[' + OFFER_PROP + ']';
  const OFFER_VALUE = 'secret-sale';

  /**
   * The cart-line attribute value a given session's discount(s) key off of. Defaults to the
   * plain-password value (`summer-sale`), but a session activated via a manual discount code
   * can carry its own `offerValue` (set at writeSession time) so it doesn't collide with the
   * automatic discount that also triggers on `summer-sale`.
   */
  function getOfferValue() {
    const session = readSession();
    return (isValidSession(session) && session.offerValue) || OFFER_VALUE;
  }

  function getCartRoutes() {
    const routes = (window.themeVariables && window.themeVariables.routes) || {};
    return {
      cartUrl: routes.cartUrl || '/cart',
      cartChangeUrl: routes.cartChangeUrl || '/cart/change'
    };
  }

  function markCartOfferSynced() {
    const session = readSession();
    if (!session) return;
    session.cartOfferSynced = true;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  }

  function needsCartOfferSync() {
    const session = readSession();
    return isValidSession(session) && !session.cartOfferSynced;
  }

  function emitCartOfferSyncEvents(cart) {
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: cart } }));
  }

  /**
   * Repeatedly finds a cart item matched by `findTarget` and rewrites its `_offer` property via
   * `/cart/change.js`, up to 25 rounds (one item fixed per round, cart re-fetched each time).
   * Stops early once `findTarget` finds nothing left to fix. Shared by the two cart-offer
   * operations below, which differ only in which items they target and what value they write.
   *
   * Shopify's `/cart/change.js` merges `properties` into the existing set rather than replacing
   * it, so omitting a key leaves its old value in place — it has to be set explicitly (including
   * to `''` to unset it).
   */
  async function reconcileCartOfferProperty(findTarget, resolveValue) {
    const routes = getCartRoutes();
    let lastCart = null;
    let done = false;
    for (let i = 0; i < 25; i++) {
      let cart;
      try {
        const cartResp = await fetch(routes.cartUrl + '.js', { credentials: 'same-origin' });
        if (!cartResp.ok) break;
        cart = await cartResp.json();
      } catch (_) {
        break;
      }
      lastCart = cart;
      const target = (cart.items || []).find(findTarget);
      if (!target) {
        done = true;
        break;
      }

      let changeOk = false;
      try {
        const changeResp = await fetch(routes.cartChangeUrl + '.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: target.key,
            quantity: target.quantity,
            properties: Object.assign({}, target.properties, { [OFFER_PROP]: resolveValue(target) }),
            sections: ['mini-cart']
          })
        });
        if (changeResp.ok) {
          lastCart = await changeResp.json();
          changeOk = true;
        }
      } catch (_) {
        changeOk = false;
      }
      if (!changeOk) break;
    }
    return { cart: lastCart, done: done };
  }

  // Clears applied coupons except the sale's own (`session.discountCode`). The
  // automatic secret-sale discount is property-based, not a code, so it's untouched.
  async function removeConflictingDiscountCodes() {
    if (typeof window.updateCartDiscountCodes !== 'function') return null;

    const session = readSession();
    const ownCode = (isValidSession(session) && session.discountCode ? String(session.discountCode).trim() : '').toLowerCase();

    let cart;
    try {
      const resp = await fetch(getCartRoutes().cartUrl + '.js', { credentials: 'same-origin' });
      if (!resp.ok) return null;
      cart = await resp.json();
    } catch (_) {
      return null;
    }

    const codes = (cart.discount_codes || [])
      .map(function (entry) { return entry && entry.code ? String(entry.code) : ''; })
      .filter(Boolean);
    if (codes.length === 0) return cart;

    // Keep only the sale's own code (none when ownCode is ''), drop the rest.
    const keep = codes.filter(function (code) { return code.toLowerCase() === ownCode; });
    if (keep.length === codes.length) return cart;

    try {
      return await window.updateCartDiscountCodes(keep.join(','));
    } catch (_) {
      return cart;
    }
  }

  let cartSyncInFlight = false;

  async function syncCartOfferOnActivate() {
    if (cartSyncInFlight || !needsCartOfferSync()) return;
    cartSyncInFlight = true;
    try {
      const offerValue = getOfferValue();
      const result = await reconcileCartOfferProperty(
        function (item) { return !(item.properties && item.properties[OFFER_PROP] === offerValue); },
        function () { return offerValue; }
      );
      if (result.done) {
        markCartOfferSynced();
        // Strip conflicting coupons now that the sale is active (keeps the sale's own code).
        const updatedCart = await removeConflictingDiscountCodes();
        const cartForEvents = updatedCart || result.cart;
        if (cartForEvents) emitCartOfferSyncEvents(cartForEvents);
      }
    } finally {
      cartSyncInFlight = false;
    }
  }

  let cartOfferClearInFlight = false;

  /**
   * Strips the `_offer` line-item property from every cart item that still carries it.
   * Called when a discount code that activated the secret sale gets removed — otherwise the
   * property lingers on existing cart lines, and a later discount attempt sees cart items that
   * already satisfy the Custom Price Discount function's eligibility check. That makes the code
   * "applicable" on the very first try (skipping the password/discount verification branch
   * entirely), so the cart discount applies but no new SecretSaleSession ever gets written.
   */
  async function clearCartOfferProperty() {
    if (cartOfferClearInFlight) return null;
    cartOfferClearInFlight = true;
    try {
      const result = await reconcileCartOfferProperty(
        function (item) { return item.properties && item.properties[OFFER_PROP]; },
        function () { return ''; }
      );
      return result.cart;
    } finally {
      cartOfferClearInFlight = false;
    }
  }

  function ensureHiddenOfferProp(form) {
    if (!form) return;
    if (form.querySelector('input[name="' + CSS.escape(INPUT_NAME) + '"]')) return;
    const i = document.createElement('input');
    i.type = 'hidden';
    i.name = INPUT_NAME;
    i.value = getOfferValue();
    form.appendChild(i);
  }

  document.addEventListener('submit', function (e) {
    if (!isActive()) return;
    const form = e.target && e.target.nodeName === 'FORM' ? e.target : null;
    if (!form || !form.matches(FORM_SELECTOR)) return;
    ensureHiddenOfferProp(form);
  }, { capture: true, passive: true });

  function runFormFlag() {
    if (!isActive()) return;
    document.querySelectorAll(FORM_SELECTOR).forEach(function (form) {
      if (form.__secretOfferPrimed) return;
      form.__secretOfferPrimed = true;
      ensureHiddenOfferProp(form);
    });
  }

  let lastPdpConfig = null;

  function initPdpFromProductInfo(config) {
    if (!config || !config.formId) return;
    function dispatchPdp() {
      document.dispatchEvent(new CustomEvent('secret-sale:apply-pdp', { detail: { config: config } }));
    }
    function tryInit() {
      if (!document.getElementById(config.formId) && !document.querySelector('form[action*="/cart/add"]')) return false;
      dispatchPdp();
      return true;
    }
    if (!tryInit()) {
      let tries = 0;
      const t = setInterval(function () {
        if (tryInit() || ++tries > 20) clearInterval(t);
      }, 50);
    }
  }

  function initDiscountBadge(badgeConfig) {
    const discountBadge = document.querySelector('#sale-percentage .sale-percentage__value') || document.querySelector('.sale-percentage__value');
    const productPageBadge = document.querySelector('.product-badge__wrapper');
    const badgeEnabled = badgeConfig && badgeConfig.badgeEnabled;
    const badgeText = (badgeConfig && badgeConfig.badgeText) || '';

    function updateUI(compare, price) {
      if (window.__secretSaleActive) return;
      if (!discountBadge) return;
      const hasDiscount = compare && price && compare > price;

      if (!hasDiscount || !discountBadge) {
        discountBadge.textContent = '';
        discountBadge.parentElement.style.display = 'none';
        discountBadge.parentElement.classList.add('visually-hidden');
      } else {
        const discount = Math.round(((compare - price) / compare) * 100);
        discountBadge.textContent = discount + '%';
        discountBadge.parentElement.style.display = '';
        discountBadge.parentElement.classList.remove('visually-hidden');
      }

      if (productPageBadge && badgeEnabled) {
        if (hasDiscount) {
          productPageBadge.style.display = '';
          const badgeEl = productPageBadge.querySelector('.product__badge');
          if (badgeEl) badgeEl.textContent = badgeText;
        } else {
          productPageBadge.style.display = 'none';
        }
      }
    }

    document.addEventListener('variant:changed', function (event) {
      if (window.__secretSaleActive) return;
      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      updateUI(variant.compare_at_price / 100, variant.price / 100);
    });
  }

  document.addEventListener('secret-sale:apply-pdp', function (e) {
    if (!e.detail || !e.detail.config) return;
    // Remembered even when inactive, so a later activation on this same PDP (no reload) has a
    // config to re-apply — see activateSecretSaleUi().
    lastPdpConfig = e.detail.config;
    if (!isActive()) return;
    window.__secretSaleActive = true;
    document.body.classList.add('secret-sale-active');
    applyPdpUi(e.detail.config);
    runFormFlag();
  });

  document.addEventListener('variant:changed', function () {
    if (!isActive()) return;
    scheduleAfterVariantPriceUpdate(function () {
      if (lastPdpConfig) applyPdpUi(lastPdpConfig);
    });
  }, { passive: true });

  document.addEventListener('secret-sale:apply-card', function (e) {
    if (!e.detail || !e.detail.root || !e.detail.config) return;
    if (!isActive()) return;
    applyCardUi(e.detail.root, e.detail.config);
  });

  function observeQuickBuy() {
    const mo = new MutationObserver(function (mutations) {
      if (!isActive()) return;
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1 || !node.querySelector) return;
          const container = node.querySelector('.qb-secret-prices');
          const pricesEl =
            container ||
            (node.querySelector && node.querySelector('.quick-buy-product-card .qb-secret-prices'));
          if (!pricesEl) return;
          const root =
            pricesEl.closest('quick-buy-drawer') ||
            pricesEl.closest('quick-buy-popover') ||
            pricesEl.closest('.drawer') ||
            node;
          initQuickBuySecretSaleRoot(root);
        });
      });
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  function bindQuickBuyReopenSecretSale() {
    document.addEventListener('openable-element:open', function (e) {
      if (!isActive()) return;
      const root = e.target;
      if (!root || (root.tagName !== 'QUICK-BUY-DRAWER' && root.tagName !== 'QUICK-BUY-POPOVER')) return;
      if (!root.querySelector('.qb-secret-prices')) return;
      initQuickBuySecretSaleRoot(root);
    });
  }

  /**
   * Re-applies all on-page secret-sale UI (form flag, product cards, cart-drawer upsell,
   * current PDP, existing-cart sync) after the session becomes active without a page reload —
   * e.g. URL auto-login or a sale password/discount code typed into the cart discount field.
   * `init()` already covers a fresh page load; this covers activation happening mid-session on
   * an already-loaded page, where cards etc. were scanned once already while inactive.
   */
  function activateSecretSaleUi() {
    if (!isActive()) return;
    window.__secretSaleActive = true;
    document.body.classList.add('secret-sale-active');
    runFormFlag();
    scanSecretSaleCardRoots(document);
    if (lastPdpConfig) applyPdpUi(lastPdpConfig);
    syncCartOfferOnActivate();
  }

  function init() {
    if (isActive()) {
      activateSecretSaleUi();
    }
    runFormFlag();
    observeQuickBuy();
    bindQuickBuyReopenSecretSale();
    scanSecretSaleCardRoots(document);
    const formMo = new MutationObserver(function () { runFormFlag(); });
    formMo.observe(document.documentElement, { childList: true, subtree: true });
    const cardMo = new MutationObserver(function (mutations) {
      if (!isActive()) return;
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          scanSecretSaleCardRoots(node);
        });
      });
    });
    cardMo.observe(document.documentElement, { childList: true, subtree: true });
    requestAnimationFrame(function () { runFormFlag(); });
    setTimeout(function () { runFormFlag(); }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.SecretSaleSession = {
    STORAGE_KEY: STORAGE_KEY,
    readSession: readSession,
    isValidSession: isValidSession,
    isActive: isActive,
    writeSession: writeSession,
    hasParam: hasParam,
    getSaleParam: getSaleParam,
    syncCartOfferOnActivate: syncCartOfferOnActivate,
    clearCartOffer: clearCartOfferProperty,
    refreshUi: activateSecretSaleUi
  };

  window.SecretSale = {
    initPdpFromProductInfo: initPdpFromProductInfo,
    initDiscountBadge: initDiscountBadge,
    verifyDiscount: verifyDiscount,
    tryActivate: tryActivate
  };
})();
